import { z } from "zod";
import { I18nText, I18nTextRequired, Id, IsoDate, LatLon, Status, Timestamp } from "./common.ts";

export const OBJECT_TYPES = [
  "figure",
  "toilet",
  "wreck",
  "vehicle",
  "platform",
  "plaque",
  "other",
] as const;
export const ObjectType = z.enum(OBJECT_TYPES);
export type ObjectType = z.infer<typeof ObjectType>;

/** Depth in metres below the water surface; positive = deeper. */
const DepthM = z.number().min(0).max(400);

/**
 * Resolved depth shown to users: `override ?? computed`.
 * `source` says which one won; both null when neither is known.
 */
export const ResolvedDepth = z
  .object({
    valueM: DepthM.nullable(),
    source: z.enum(["override", "computed"]).nullable(),
  })
  .strict()
  .refine((d) => (d.valueM === null) === (d.source === null), "valueM and source null together");

export const DiveObject = z
  .object({
    id: Id,
    spotId: Id,
    type: ObjectType,
    name: I18nTextRequired,
    description: I18nText,
    location: LatLon,
    /** From swissBATHY3D at `location`; null if no bathy/nodata. Server-owned. */
    depthComputedM: DepthM.nullable(),
    /** Measured by a diver, set by a moderator. Wins over computed. */
    depthOverrideM: DepthM.nullable(),
    depth: ResolvedDepth,
    /** Most recent diver confirmation ("still there" / "gone"); null if never reported. */
    lastSighting: z.object({ seenOn: IsoDate, present: z.boolean() }).strict().nullable(),
    /** Number of sighting reports (all divers, all days). */
    sightingCount: z.number().int().min(0),
    status: Status,
    createdAt: Timestamp,
    updatedAt: Timestamp,
  })
  .strict()
  .refine(
    (o) => {
      const expected = o.depthOverrideM ?? o.depthComputedM;
      const source =
        o.depthOverrideM !== null ? "override" : o.depthComputedM !== null ? "computed" : null;
      return o.depth.valueM === expected && o.depth.source === source;
    },
    { message: "depth must equal override ?? computed with the matching source", path: ["depth"] },
  )
  .refine((o) => (o.lastSighting === null) === (o.sightingCount === 0), {
    message: "lastSighting is null exactly when sightingCount is 0",
    path: ["lastSighting"],
  });
export type DiveObject = z.infer<typeof DiveObject>;

/** POST /api/objects. `depthComputedM` is never accepted from clients. */
export const DiveObjectCreate = z
  .object({
    spotId: Id,
    type: ObjectType,
    name: I18nTextRequired,
    description: I18nText.default({}),
    location: LatLon,
    depthOverrideM: DepthM.nullable().default(null),
    status: Status.default("draft"),
  })
  .strict();
export type DiveObjectCreate = z.input<typeof DiveObjectCreate>;

/** PATCH /api/objects/:id. Changing `location` makes the server recompute depth. */
export const DiveObjectUpdate = z
  .object({
    type: ObjectType,
    name: I18nTextRequired,
    description: I18nText,
    location: LatLon,
    depthOverrideM: DepthM.nullable(),
    status: Status,
  })
  .partial()
  .strict();
export type DiveObjectUpdate = z.infer<typeof DiveObjectUpdate>;

/**
 * POST /api/objects/:id/sightings — a diver confirms the object was (or was no longer) there.
 * One report per diver, object and day: a second report the same day replaces the first.
 * `lastSighting` = the report with the latest `seenOn`, ties broken by latest submission;
 * `sightingCount` = stored reports after replacement. "Gone missing" is not a separate
 * flag: clients show it when `lastSighting.present === false`.
 * `seenOn` may not be in the future (Europe/Zurich) nor older than 2 years → 400.
 */
export const SightingCreate = z.object({ seenOn: IsoDate, present: z.boolean() }).strict();
export type SightingCreate = z.infer<typeof SightingCreate>;

/** GET /api/objects/:id/sightings — public history, newest first, max 50; no reporter identity. */
export const SightingList = z
  .object({ sightings: z.array(z.object({ seenOn: IsoDate, present: z.boolean() }).strict()) })
  .strict();
export type SightingList = z.infer<typeof SightingList>;
